import type { PlanV2IssueDispositionRecord } from './plan-v2-issues';

/**
 * Site-wide technical defects, asked about once per period instead of once per
 * page — once they have been SENT.
 *
 * A template defect is reported by the crawler on every page it touches — on
 * manyrequests.com six of them account for 2,597 of 3,172 findings across 336
 * pages. A reviewer walking forty-five URLs is asked the same six questions
 * forty-five times, and after the first answer every repeat is noise that the
 * genuinely page-specific findings have to compete with.
 *
 * So the answer is recorded against the CODE for the period. Queueing it as
 * work stops it being shown at all — the fix is already on the list, and asking
 * again would be asking for it twice. Putting it aside is the weaker answer and
 * is handled elsewhere: the check keeps appearing, already unticked, so a
 * reviewer can change their mind on the page where it matters. See
 * `utils/review-check-memory`.
 *
 * The consequence is deliberate and worth stating plainly: page 30 of a review
 * shows fewer technical findings than the page actually has. That is correct
 * for triage — you already decided — and wrong for an audit, which is why
 * **Inventory always shows everything** and only the review surface suppresses.
 */

const SITE_WIDE_KEY_PREFIX = 'sitewide::';

/** The disposition key for one site-wide code. */
export function siteWideIssueKey(code: string): string {
  return `${SITE_WIDE_KEY_PREFIX}${(code || '').trim()}`;
}

/** The code back out of a key, or '' when the key is not one of ours. */
export function siteWideCodeFromKey(issueKey: string): string {
  const key = (issueKey || '').trim();
  return key.startsWith(SITE_WIDE_KEY_PREFIX) ? key.slice(SITE_WIDE_KEY_PREFIX.length) : '';
}

/**
 * Codes already QUEUED AS WORK this period.
 *
 * Read from the queue service's own records rather than counted locally, so a
 * decision made in an earlier session — or in another tab — suppresses just the
 * same.
 *
 * Only `work_item` counts, and that is a change from how this started. It used
 * to treat both answers as final: sending the defect and putting it aside both
 * stopped it being asked. Putting aside is a weaker statement than that — it
 * says "not this one", not "never again" — and an operator who unticks a check
 * on page 1 has to be able to tick it on page 9 when they hit the page where it
 * actually matters. So a put-aside site-wide check is MUTED rather than
 * settled: it keeps appearing, unticked. See `utils/review-check-memory`.
 *
 * Sending stays final, because a template defect is one fix. Once it is queued,
 * asking again on the next page is asking the operator to queue the same work a
 * second time.
 */
export function decidedSiteWideCodes(
  records: Iterable<PlanV2IssueDispositionRecord> | null | undefined
): Set<string> {
  const codes = new Set<string>();
  for (const record of records ?? []) {
    if (!record || record.issueType !== 'site_wide_technical') continue;
    if (record.queue !== 'work_item') continue;
    const code = siteWideCodeFromKey(record.issueKey);
    if (code) codes.add(code);
  }
  return codes;
}

export interface SiteWideSplit<T> {
  /** Still worth asking about on this page. */
  open: T[];
  /** Already answered this period — shown as a count, not as questions. */
  settled: T[];
}

/**
 * Split a page's findings into what still needs an answer and what does not.
 *
 * The settled ones are returned rather than dropped so the card can say how
 * many it is holding back. Hiding them silently would leave an operator
 * wondering why a page they know is broken looks clean.
 */
export function splitSettledSiteWide<T extends { code: string; scope: string }>(
  issues: readonly T[] | null | undefined,
  decided: ReadonlySet<string>
): SiteWideSplit<T> {
  const open: T[] = [];
  const settled: T[] = [];
  for (const issue of issues ?? []) {
    if (issue.scope === 'site-wide' && decided.has(issue.code)) settled.push(issue);
    else open.push(issue);
  }
  return { open, settled };
}

/** "6 site-wide issues — already queued this period." */
export function settledSiteWideLabel(count: number): string | null {
  if (count <= 0) return null;
  return count === 1
    ? '1 site-wide issue — already decided this period.'
    : `${count} site-wide issues — already decided this period.`;
}
